import React, { useEffect, useRef } from 'react';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import { Image as BaseImage } from '@tiptap/extension-image';
import { Editor } from '@tiptap/core';
import { Wrench } from 'lucide-react';

type ResizeAnchor = 'br' | 'bl' | 'tr' | 'tl';

const ResizableImageView: React.FC<any> = ({ node, updateAttributes, selected, editor, getPos }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const startRef = useRef<{ anchor: ResizeAnchor; startX: number; rect: DOMRect } | null>(null);

  const startResize = (anchor: ResizeAnchor, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    startRef.current = { anchor, startX: e.clientX, rect };

    const onMove = (ev: MouseEvent) => {
      if (!startRef.current || !imgRef.current) return;
      const { anchor, rect } = startRef.current;
      let newWidth = imgRef.current.width;

      if (anchor === 'br' || anchor === 'tr') {
        newWidth = Math.max(50, ev.clientX - rect.left);
      } else {
        newWidth = Math.max(50, rect.right - ev.clientX);
      }

      updateAttributes({ width: Math.round(newWidth) });
    };

    const onUp = () => {
      startRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const width = node.attrs.width ? `${node.attrs.width}px` : undefined;

  useEffect(() => {
    if (!imgRef.current) return;
    const base = 'max-width:100%;height:auto;display:block;';
    const w = node.attrs.width ? `width:${node.attrs.width}px;` : '';
    const custom = typeof node.attrs.style === 'string' ? node.attrs.style : '';
    imgRef.current.setAttribute('style', `${base}${w}${custom}`);
  }, [node.attrs.width, node.attrs.style]);

  return (
    <NodeViewWrapper className={`mw-image-resizer group relative inline-block ${selected ? 'ring-2 ring-blue-400' : ''}`}>
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ''}
        style={{ width, maxWidth: '100%', height: 'auto', display: 'block' }}
        draggable={false}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            if (editor && typeof getPos === 'function') {
              editor.chain().focus().setNodeSelection(getPos()).run();
            }
          } catch {
            // no-op
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            if (editor && typeof getPos === 'function') {
              editor.chain().focus().setNodeSelection(getPos()).run();
            }
          } catch {
            // no-op
          }
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            if (editor && typeof getPos === 'function') {
              editor.commands.setNodeSelection(getPos());
              editor.chain().focus().run();
            }
          } catch {
            // no-op
          }
        }}
      />
      {/* Overlay tools button (visible on hover) */}
      <div
        className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-auto"
        onMouseDown={(e) => {
          // Prevent starting a resize when clicking tools
          e.stopPropagation();
        }}
      >
        <button
          type="button"
          title="Image tools"
          className="inline-flex items-center justify-center h-7 w-7 rounded-md border bg-background/80 text-foreground shadow-sm hover:bg-background transition-colors"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              if (editor && typeof getPos === 'function') {
                // Select this image node and focus
                editor.chain().focus().setNodeSelection(getPos()).run();
              }
            } catch {
              // no-op
            }
            // Notify the editor to open image tools
            window.dispatchEvent(new CustomEvent('rte-open-image-tools'));
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              if (editor && typeof getPos === 'function') {
                editor.chain().focus().setNodeSelection(getPos()).run();
              }
            } catch {
              // no-op
            }
          }}
        >
          <Wrench className="h-4 w-4" />
        </button>
      </div>
      {/* Corner handles */}
      <span className="mw-handle mw-handle-tl" onMouseDown={(e) => startResize('tl', e)} />
      <span className="mw-handle mw-handle-tr" onMouseDown={(e) => startResize('tr', e)} />
      <span className="mw-handle mw-handle-bl" onMouseDown={(e) => startResize('bl', e)} />
      <span className="mw-handle mw-handle-br" onMouseDown={(e) => startResize('br', e)} />
    </NodeViewWrapper>
  );
};

const ResizableImage = BaseImage.extend({
  name: 'resizableImage',
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          // Keep width attribute for editor serialization; NodeView handles visual style.
          return { width: attributes.width };
        },
        parseHTML: (element) => {
          const w = element.getAttribute('width');
          if (w) return parseInt(w, 10);
          // Fallback: parse inline style width (e.g., style="width: 320px")
          const style = element.getAttribute('style') || '';
          const m = style.match(/width\s*:\s*(\d+)\s*px/i);
          return m ? parseInt(m[1], 10) : null;
        },
      },
      alt: { default: null },
      src: { default: null },
      style: {
        default: '',
        renderHTML: (attributes) => {
          return attributes.style ? { style: attributes.style } : {};
        },
        parseHTML: (element) => element.getAttribute('style') || '',
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
}) as unknown as Editor;

export default ResizableImage;